import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { useSdk } from '~/sdk';
import type { GetCarriersResponse, GetCarriersResponseItem, UpdateCheckoutCarrierRequest } from '@vue-storefront/prestashop-api';
import type { Reactive } from 'vue';
import { cloneDeep } from 'lodash-es';

export const useCarriers = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['carriers'],
    queryFn: async (): Promise<GetCarriersResponse['psdata']> => {
      return await useSdk().commerce.getShippingMethods();
    },
  });

  // Update carrier mutation
  const mutation = useMutation({
    mutationFn: async (data: UpdateCheckoutCarrierRequest) => {
      return await useSdk().commerce.updateCheckoutCarrier({
        ...data,
      });
    },
    onMutate: async (data: UpdateCheckoutCarrierRequest) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['carriers'] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['carriers']);

      const newData = cloneDeep({ ...unref(query.data.value) });

      Object.keys(newData).forEach((key) => {
        newData[key].selected = false;
        newData[key].selected = data.id_carrier == newData[key].id;
      });

      // Optimistically update to the new value
      queryClient.setQueryData(['carriers'], newData);

      // Return a context with the previous and new todo
      return { previousData, newData };
    },
    onError: (err, newTodo, context) => {
      queryClient.setQueryData(['carriers'], context?.previousData);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['cart'] });
      queryClient.invalidateQueries({ queryKey: ['carriers'] });
      queryClient.invalidateQueries({ queryKey: ['pudos'] });
    },
  });

  return {
    query: reactive(query) satisfies Reactive<typeof query>,
    mutation: reactive(mutation) satisfies Reactive<typeof mutation>,
    currentCarrier: computed(() => {
      return (
        Object.values(query.data.value ?? {}).find((carrier) => {
          return carrier.selected;
        }) ?? null
      );
    }) satisfies ComputedRef<GetCarriersResponseItem | null>,

    totalCarriers: computed(() => {
      return Object.values(query.data.value ?? {}).length;
    }) satisfies ComputedRef<number>,
  };
};
